import { RouteComponentProps, withRouter } from 'react-router-dom'

import { OnAvailableFundingSources } from '@acorns/web-bank-linking'
import { ModalActionTypes } from '@acorns/web-components'
import { WithOptimizelyProps } from '@optimizely/react-sdk'
import debug from 'debug'
import { compose, withHandlers } from 'recompose'

import withPartnerProps, { WithPartnerProps } from 'components/HOCs/WithPartnerProps'
import getDefaultEventData from 'components/Trackers/defaults'
import fundingSourceInfoQuery from 'pages/Institutions/gql/FundingSourceInfo.query.gql'
import trackEvent from 'utils/analytics/trackEvent'
import Environment, { withEnv } from 'utils/environment'
import { sendToSentry } from 'utils/errors'
import { WithFlowRouterProps, withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'
import { withSetPrimaryFundingSource } from 'utils/mutators/bank-linking/bankLinking'
import { withOptimizely } from 'utils/Optimizely'
import { withPrompt } from 'utils/prompts'

const logger = debug('institutions')

type Props = {
  env: Environment
  loading: boolean
}

type HandlerProps = {
  handleAvailableFundingSource: OnAvailableFundingSources
  handleLinkAccountError: (error: any) => void
  onPlaidLinkEvent: () => void
  onPlaidLinkSuccess: () => void
}

type PlaidLinkProps = Props &
  HandlerProps &
  WithFlowRouterProps &
  RouteComponentProps &
  WithPartnerProps &
  WithOptimizelyProps

const withPlaidLinkProps = compose<PlaidLinkProps, {}>(
  withEnv,
  withRouter,
  withFlowRouter,
  withPartnerProps,
  withSetPrimaryFundingSource,
  withOptimizely,
  withPrompt,
  withHandlers({
    handleAvailableFundingSource: ({
      partner,
      route,
      setPrimaryFundingSource,
      promptUser,
      trackOptimizely,
      flow,
      isRedirect,
    }) => async ({ financialInstitution, linkedSubaccounts }) => {
      const fromRoute = isRedirect ? FlowRoute.PlaidLinkRedirect : FlowRoute.Institutions

      if (linkedSubaccounts.length > 1) {
        logger('More than 1 available funding source')

        route({
          from: fromRoute,
          action: 'choose',
          locationState: { financialInstitution, linkedSubaccounts, fromRedirectLink: isRedirect },
        })
      } else if (linkedSubaccounts.length === 1) {
        logger('1 available funding source')
        const { id: linkedSubaccountId } = linkedSubaccounts[0]

        await setPrimaryFundingSource(linkedSubaccountId, fundingSourceInfoQuery).then(() => {
          trackOptimizely('funding_source.linked')
          trackEvent('PlaidLink:Success', { location, partner, flow })
          route({
            from: fromRoute,
            action: 'continue',
            locationState: {
              financialInstitution,
              linkedSubaccounts,
              fromRedirectLink: isRedirect,
            },
          })
        })
      } else if (linkedSubaccounts.length === 0) {
        logger('no available funding sources')
        trackOptimizely('roundup_account.linked')

        const linkedForCopy = flow === 'later' ? '' : ' for Round Ups'

        promptUser({
          title: 'Link Funding Source',
          message: `We linked your account${linkedForCopy}. However, no checking accounts were found with that bank. A valid checking account is required as funding source`,
          actions: [
            {
              type: ModalActionTypes.primary,
              label: 'Okay',
            },
          ],
        }).then(() =>
          route({
            from: fromRoute,
            action: 'manual',
            locationState: { financialInstitution, fromRedirectLink: isRedirect },
          }),
        )
      }
    },
    handleLinkAccountError: () => error => {
      sendToSentry(error, { breadcrumb: 'User is attempting to create Plaid linked account.' })
    },
    onPlaidLinkSuccess: ({ setSubmitting, trackOptimizely }) => () => {
      setSubmitting(true)
      trackOptimizely('banklinking.complete')
    },
    onPlaidLinkEvent: ({ partner, flow }) => (event, meta) => {
      trackEvent('Plaid Event', {
        eventData: {
          ...getDefaultEventData(event),
          ...meta,
        },
        location,
        partner,
        flow,
      })
    },
  }),
)

export { withPlaidLinkProps, PlaidLinkProps }
